import * as React from 'react'

import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

import {
	DataGridPremium,
	type DataGridPremiumProps,
	type GridColDef,
	type GridValidRowModel,
} from '@mui/x-data-grid-premium'

export type DataGridProps = {
	columns: GridColDef[],
	rows: readonly GridValidRowModel[] | null | undefined,
	isLoading?: boolean,
	emptyStateText?: string,
} & Omit<Partial<DataGridPremiumProps>, 'columns' | 'rows' | 'loading'>

function makeNoRowsOverlay( text: string ): () => React.ReactElement {
	return function NoRowsOverlay(): React.ReactElement {
		return (
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					height: '100%',
					p: 2,
				}}
			>
				<Typography variant="body2" color="text.secondary">{text}</Typography>
			</Box>
		)
	}
}

/**
 * Shared MUI DataGrid wrapper. Mirrors the EUI `Table` API
 * (`{ columns, rows, isLoading, emptyStateText }`) while exposing the full
 * `DataGridPremium` surface for server-side use cases.
 *
 * Defaults to `density="compact"` and `autoHeight` (the grid sizes to its rows
 * rather than filling a fixed-height container) and renders `emptyStateText`
 * when there are no rows. For parity with the EUI `Table` it replaces, the
 * column menu, column sorting, and the footer are all disabled by default —
 * those affordances are deferred to the DataGrid redline, not introduced by the
 * migration. The outer border is also removed (EUI tables had no outer frame —
 * only internal row/header separators, which are kept). A table that wants sorting opts in with `disableColumnSorting={false}`
 * plus `sortable` on the relevant `GridColDef`s. Height modes: the default
 * `autoHeight` sizes the grid to its rows (short lists hug their content). For a
 * fixed scroll region with a **pinned header** — the EUI table behavior this
 * replaces, an app-level `.euiBasicTable` sticky-header override — pass
 * `autoHeight={false}` inside a fixed-height container (`<Box sx={{ height }}>`);
 * the grid then fills that height, scrolls its rows internally with the header
 * pinned, and virtualizes rows. (A sticky header is only possible in this mode:
 * the grid root's `transform`/`overflow` trap a CSS `position: sticky` header in
 * `autoHeight` mode.) Any additional `DataGridPremium` prop is passed straight
 * through —
 * this is the server-side escape hatch: pass `hideFooter={false}` +
 * `pagination` + `paginationMode="server"`, `rowCount`, `paginationModel`,
 * `onPaginationModelChange`, etc. to bypass client-side pagination (the footer
 * must be re-enabled to show the pagination controls). `disableVirtualization`
 * defaults to `true` under Jest, since DataGridPremium's virtualization silently
 * drops rows past a small threshold in jsdom.
 */
export default function DataGrid( {
	columns,
	rows,
	isLoading = false,
	emptyStateText = 'No records found',
	density = 'compact',
	// DataGridPremium virtualization silently drops rows past a small
	// threshold in jsdom (no thrown error), so callers relying on rows
	// beyond that threshold get tests that pass for the wrong reason.
	// Default it off under Jest; any caller can still override explicitly.
	disableVirtualization = process.env.NODE_ENV === 'test',
	slots,
	sx,
	...rest
}: DataGridProps ): React.ReactElement {
	const NoRowsOverlay = React.useMemo(
		() => makeNoRowsOverlay( emptyStateText ),
		[ emptyStateText ],
	)

	return (
		<DataGridPremium
			columns={columns}
			rows={rows ?? []}
			loading={isLoading}
			density={density}
			autoHeight
			disableColumnMenu
			disableColumnSorting
			disableRowSelectionOnClick
			disableVirtualization={disableVirtualization}
			hideFooter
			slots={{ noRowsOverlay: NoRowsOverlay, ...slots }}
			// Drop the outer frame (EUI tables had none); internal row/header
			// separators are kept. Caller `sx` is merged last so it can override.
			sx={[ { border: 0 }, ...( Array.isArray( sx ) ? sx : [ sx ] ) ]}
			{...rest}
		/>
	)
}
